import LegalLayout from "@/components/LegalLayout";
import useSiteContent from "@/hooks/useSiteContent";

const TOC = [
    { id: "who", label: "1. Who we are" },
    { id: "data", label: "2. Data we collect" },
    { id: "uses", label: "3. How we use it" },
    { id: "sharing", label: "4. Sharing & processors" },
    { id: "storage", label: "5. Storage & retention" },
    { id: "security", label: "6. Security" },
    { id: "rights", label: "7. Your rights" },
    { id: "cookies", label: "8. Cookies & analytics" },
    { id: "children", label: "9. Children" },
    { id: "changes", label: "10. Changes" },
    { id: "contact", label: "11. Contact" },
];

export default function Privacy() {
    const { content } = useSiteContent();
    if (content.privacy_html && content.privacy_html.trim()) {
        return (
            <LegalLayout title="Privacy Policy" updated="Live · CMS" toc={TOC}>
                <div data-testid="privacy-cms-body" dangerouslySetInnerHTML={{ __html: content.privacy_html }} />
            </LegalLayout>
        );
    }
    return (
        <LegalLayout title="Privacy Policy" updated="February 2026" toc={TOC}>
            <p className="text-sm text-[#17212B]/70">
                Standard privacy policy. Describes what we collect and how we
                handle it — subject to legal-counsel refinement before final
                public launch.
            </p>

            <h2 id="who">1. Who we are</h2>
            <p>
                &ldquo;We&rdquo; refers to <strong>HUMANITYFIRST SOFTWARE
                TECHNOLOGY PRIVATE LIMITED</strong> (the Company), operator of
                the Doctor Digital Identity Platform. Contact:{" "}
                <a href="tel:+917722090290">+91 7722090290</a>, registered
                address A-803 Palatial Height, Chandivali Farm Road, Andheri,
                Mumbai, Maharashtra 400072, India.
            </p>

            <h2 id="data">2. Data we collect</h2>
            <ul>
                <li><strong>Account data</strong>: name, email, mobile, medical registration number, city / state, and password (stored hashed via bcrypt).</li>
                <li><strong>Professional profile</strong>: qualifications, education, experience, awards, publications, memberships, affiliations, biography notes, consultation info, and any photos / documents you upload.</li>
                <li><strong>Payment records</strong>: order IDs, invoice numbers, amounts, gateway transaction references. <strong>We do not store</strong> full card / UPI / bank credentials — those live with our payment aggregator.</li>
                <li><strong>Usage / analytics</strong>: page views, feature usage, device / browser type, IP address, geo-region (city-level).</li>
                <li><strong>Communications</strong>: support tickets, in-app messages, feedback.</li>
            </ul>

            <h2 id="uses">3. How we use it</h2>
            <ul>
                <li>To operate the Service, render your public identity page, and process payments.</li>
                <li>To compose AI-generated copy (biography, summary, FAQs, SEO) grounded strictly in the data you provide.</li>
                <li>To send transactional communications (OTP, payment receipts, publish notifications, AMC reminders).</li>
                <li>To improve platform quality via aggregated, non-identifying analytics.</li>
                <li>To respond to legal requests, prevent fraud, and enforce our <a href="/terms">Terms</a>.</li>
            </ul>
            <p>
                <strong>We do not sell your personal data.</strong> We do not use your data to train third-party AI models.
            </p>

            <h2 id="sharing">4. Sharing &amp; processors</h2>
            <p>Necessary sub-processors:</p>
            <ul>
                <li><strong>Cashfree Payments India Pvt. Ltd.</strong> — payment processing (PCI-scoped data).</li>
                <li><strong>Cloud hosting</strong> — application and database hosting (data centres in India / Asia region).</li>
                <li><strong>Anthropic / Google (Gemini) / OpenAI</strong> — AI text generation via the Emergent Universal Key. Only the structured profile fields we send get processed; content is composed and returned in real time. AI providers do not receive your login credentials or payment data.</li>
                <li><strong>Support / analytics tooling</strong> — bug tracking and product analytics (anonymised where possible).</li>
            </ul>
            <p>
                We share personal data with processors only to the extent
                needed for the specific service, under written contracts that
                bind them to confidentiality and data-security obligations.
                We do not share data with third parties for their independent
                marketing.
            </p>

            <h2 id="storage">5. Storage &amp; retention</h2>
            <p>
                Data is stored on servers physically located in India / Asia
                region. We retain your account and content data for as long as
                your account is active, and for a reasonable period thereafter
                to meet legal, tax, and audit obligations (typically up to 7
                years for financial records; profile data purged within 90 days
                of account closure unless legally required to retain).
            </p>

            <h2 id="security">6. Security</h2>
            <ul>
                <li>Passwords are hashed (bcrypt) — never stored in plain text.</li>
                <li>Data in transit is encrypted (HTTPS / TLS).</li>
                <li>Payment credentials are handled by the payment aggregator; we hold only the aggregator&rsquo;s reference IDs.</li>
                <li>Least-privilege access to production data; audit logs on admin actions.</li>
            </ul>

            <h2 id="rights">7. Your rights</h2>
            <p>Subject to applicable law (including the DPDP Act, 2023), you may:</p>
            <ul>
                <li>Access the personal data we hold about you.</li>
                <li>Correct inaccurate data via the in-app onboarding wizard or Settings.</li>
                <li>Request deletion of your account and personal data (subject to retention obligations above).</li>
                <li>Withdraw consent for processing (may limit the Service&rsquo;s functionality).</li>
                <li>Nominate a person to exercise these rights in the event of incapacity.</li>
                <li>Raise a grievance — see <a href="/contact">/contact</a>.</li>
            </ul>

            <h2 id="cookies">8. Cookies &amp; analytics</h2>
            <p>
                We use essential cookies (session, authentication) and
                privacy-respecting analytics. We do not run cross-site
                advertising trackers.
            </p>

            <h2 id="children">9. Children</h2>
            <p>
                The Service is intended for medical practitioners and is not
                directed to persons under 18. We do not knowingly collect
                personal data from children.
            </p>

            <h2 id="changes">10. Changes to this policy</h2>
            <p>
                We&rsquo;ll notify you of material changes via email or an
                in-app banner. The &ldquo;Last updated&rdquo; date at the top
                reflects the most recent revision.
            </p>

            <h2 id="contact">11. Contact / data requests</h2>
            <p>
                Data protection / grievance officer: Doctor Digital Identity
                Platform, HUMANITYFIRST SOFTWARE TECHNOLOGY PRIVATE LIMITED,
                A-803 Palatial Height, Chandivali Farm Road, Andheri, Mumbai,
                Maharashtra 400072, India. Phone:{" "}
                <a href="tel:+917722090290">+91 7722090290</a>. Please mark
                data requests as such in the subject line.
            </p>
        </LegalLayout>
    );
}
